/**
 * Preview/test `AiProvider`: an in-memory implementation of the connection
 * contract (AI-CONNECT-001.2). It exists so the UI can be wired and tested
 * before the backend is on `main`. It is NOT sample UI data in disguise: it
 * enforces the contract's rules (a quote is not a hold, an idempotent run
 * replays its stored turn - even after the quote expired - a pending run keeps
 * its hold, a new key on an expired quote is rejected, retention only shrinks)
 * and raises the backend's own error codes with the backend's HTTP statuses.
 *
 * Nothing is persisted and no request is made. It must never be mounted in a
 * live build (`selectProvider` has no path from `live` to here).
 */
import {
  AI_ERROR_MESSAGES,
  AI_FEATURE_ERP_ANALYSIS,
  AI_NO_DATA_MESSAGE,
  type AiActivation,
  type AiBalance,
  type AiBudget,
  type AiBudgetUpdate,
  type AiConversion,
  type AiEvidence,
  type AiMoney,
  type AiQuote,
  type AiQuoteRequest,
  type AiRunRecovery,
  type AiRunRequest,
  type AiRunStatus,
  type AiSettings,
  type AiSettingsUpdate,
  type AiTurn,
  type AiUsageResponse,
  type AiUsageScope,
  type AiWalletPackagesResponse,
  type AiTopupCheckoutRequest,
  type AiTopupCheckoutResponse,
  type AiTopupVerifyResponse,
  type AiTopupOrder,
} from './contract.ts';
import { formatAmountToman } from './display.ts';
import { AiAbortedError, AiContractError, AiNetworkError } from './errors.ts';
import {
  FIXTURE_SCENARIOS,
  FIXTURE_STOCK,
  LATE_SETTLEMENT,
  STATE_LABELS,
  turnFromScenario,
  type FixtureScenario,
} from './fixtures.ts';
import type { AiProvider } from './provider.ts';

const QUOTE_TTL_MS = 5 * 60 * 1000;
const MAX_RETENTION_DAYS = 30;
const GENERIC_REJECTION = 'درخواست انجام نشد.';

export interface FixtureOptions {
  /** Micro-credits in the wallet. */
  readonly availableMicroCredits?: bigint;
  /** `null` = no stored conversion (the «تعیین نشده» display). */
  readonly conversion?: AiConversion | null;
  readonly hardBudgetMicroCredits?: bigint;
  readonly softBudgetMicroCredits?: bigint;
  readonly retentionDays?: number;
  /** Which explicit run outcome `createRun` returns (see `fixtures.ts`). Default `settled`. */
  readonly scenario?: FixtureScenario;
  /** Stock rows the analysis sees; defaults to one row, or none for the `no-data` scenario. */
  readonly stock?: readonly AiEvidence[];
  /** Permission keys of the caller (ai.wallet.read, ai.run.execute, …). Default: all. */
  readonly permissions?: ReadonlySet<string>;
  readonly active?: boolean;
  readonly optedIn?: boolean;
  readonly killSwitch?: boolean;
  /** Test clock. */
  readonly now?: () => Date;
}

export const FIXTURE_CONVERSION: AiConversion = {
  microCreditsPerToman: '4',
  version: '1',
  rounding: 'half_up',
};

const ALL_PERMISSIONS: ReadonlySet<string> = new Set([
  'ai.wallet.read',
  'ai.budget.manage',
  'ai.settings.manage',
  'ai.activation.manage',
  'ai.run.execute',
  'ai.usage.read',
  'ai.usage.read-all',
  'inventory.stock.read',
]);

/** Half-up integer division, as the contract defines it. */
function halfUp(microCredits: bigint, perToman: bigint): bigint {
  return (microCredits + perToman / 2n) / perToman;
}

export function moneyFrom(microCredits: bigint, conversion: AiConversion | null): AiMoney {
  if (conversion === null) {
    return { microCredits: microCredits.toString(), toman: null, suppressedBelowOneToman: false };
  }
  const toman = halfUp(microCredits, BigInt(conversion.microCreditsPerToman));
  if (microCredits > 0n && toman === 0n) {
    return { microCredits: microCredits.toString(), toman: null, suppressedBelowOneToman: true };
  }
  return {
    microCredits: microCredits.toString(),
    toman: toman.toString(),
    suppressedBelowOneToman: false,
  };
}

export interface FixtureProvider extends AiProvider {
  /** Test/preview hook: changes the stock the analysis sees (invalidates open quotes). */
  setStock(rows: readonly AiEvidence[]): void;
  /** Test hook: the NEXT `createRun` is recorded but its response is lost (a network error). */
  loseNextRunResponse(): void;
  /** Test hook: the late usage arrives for a pending run (`settleLateUsage`). */
  settleLate(runId: string): void;
}

interface RunRecord {
  readonly idempotencyKey: string;
  readonly quoteId: string;
  readonly createdAt: string;
  turn: AiTurn;
  execution: AiRunStatus['execution'];
  settlement: AiRunStatus['settlement'];
}

type Outer = 'NOT_FOUND' | 'FORBIDDEN' | 'VALIDATION_FAILED';

export function createFixtureProvider(options: FixtureOptions = {}): FixtureProvider {
  const now = options.now ?? (() => new Date());
  const conversion = options.conversion === undefined ? FIXTURE_CONVERSION : options.conversion;
  const permissions = options.permissions ?? ALL_PERMISSIONS;
  const scenario: FixtureScenario = options.scenario ?? 'settled';
  let stock: readonly AiEvidence[] = options.stock ?? (scenario === 'no-data' ? [] : FIXTURE_STOCK);
  const actor = 'fixture-actor';

  let available = options.availableMicroCredits ?? 1_000_000n;
  let consumed = 0n;
  let openHolds = 0n;
  let hard = options.hardBudgetMicroCredits ?? 5_000_000n;
  let soft = options.softBudgetMicroCredits ?? 1_000_000n;
  let retentionDays = options.retentionDays ?? MAX_RETENTION_DAYS;
  let optedIn = options.optedIn ?? true;
  const active = options.active ?? true;
  const killSwitch = options.killSwitch ?? false;
  let disposed = false;
  let loseNext = false;

  const quotes = new Map<
    string,
    AiQuote & { actor: string; expiresAtMs: number; question: string }
  >();
  const runsByKey = new Map<string, RunRecord>();
  const runsById = new Map<string, RunRecord>();
  const turnsById = new Map<string, RunRecord>();
  const ordered: RunRecord[] = [];
  let counter = 0;
  const nextId = (prefix: string) => `${prefix}-${(counter += 1)}`;

  /** A backend `AiCreditError`: outer code and status as `mapAiError` assigns them. */
  function reject(status: number, outer: Outer, detailCode: string, path = 'ai'): never {
    const message = AI_ERROR_MESSAGES[detailCode] ?? GENERIC_REJECTION;
    throw new AiContractError(status, outer, [{ path, code: detailCode, message }], nextId('req'));
  }
  const conflict = (code: string): never => reject(409, 'VALIDATION_FAILED', code);
  const invalid = (code: string, path = 'ai'): never =>
    reject(422, 'VALIDATION_FAILED', code, path);

  function alive(): void {
    if (disposed) throw new AiAbortedError(false);
  }

  /** Missing permission: `FORBIDDEN 403` without `details`; the message is the permission key. */
  function require(...keys: readonly string[]): void {
    alive();
    if (!keys.some((key) => permissions.has(key))) {
      throw new AiContractError(403, 'FORBIDDEN', [], nextId('req'), keys[0]);
    }
  }

  function requireAll(...keys: readonly string[]): void {
    for (const key of keys) require(key);
  }

  const fingerprintOf = (question: string) =>
    `fp:${stock.map((row) => `${row.recordId}=${row.onHand}`).join(',')}:${question}`;

  function ready(): void {
    if (!active || !optedIn || killSwitch) reject(403, 'FORBIDDEN', 'TENANT_AI_DISABLED');
  }

  const activation = (): AiActivation => ({
    active,
    optedIn,
    killSwitch,
    tenantActive: true,
    features: [
      { code: AI_FEATURE_ERP_ANALYSIS, implemented: true, executable: true },
      { code: 'ai.sales.analysis', implemented: false, executable: false },
      { code: 'ai.procurement.analysis', implemented: false, executable: false },
      { code: 'ai.accounting.analysis', implemented: false, executable: false },
      { code: 'ai.projects.analysis', implemented: false, executable: false },
    ],
  });

  function statusOf(record: RunRecord): AiRunStatus {
    const { turn } = record;
    return {
      runId: turn.runId ?? '',
      turnId: turn.turnId,
      quoteId: record.quoteId,
      state: turn.state,
      stateLabel: turn.stateLabel,
      execution: record.execution,
      settlement: record.settlement,
      usage: turn.usage,
      amountToman: turn.amountToman,
      amountLabel: turn.amountLabel,
      featureCode: AI_FEATURE_ERP_ANALYSIS,
    };
  }

  function settingsView(): AiSettings {
    return {
      optedIn,
      retentionDays,
      killSwitch,
      chatProviderCode: null,
      chatModelCode: null,
      chatModelVersion: null,
      importMappingFree: true,
      importMappingCustomerChargeToman: '0',
    };
  }

  return {
    kind: 'fixture',

    setStock(rows) {
      stock = rows;
    },

    loseNextRunResponse() {
      loseNext = true;
    },

    settleLate(runId) {
      const record = runsById.get(runId);
      if (!record || record.settlement.status !== 'usage_unknown') return;
      const held = BigInt(record.settlement.held.microCredits);
      openHolds -= held;
      const charge = BigInt(LATE_SETTLEMENT.charge.microCredits);
      available -= charge;
      consumed += charge;
      record.execution = LATE_SETTLEMENT.execution;
      record.settlement = LATE_SETTLEMENT.settlement;
      record.turn = {
        ...record.turn,
        state: 'settled',
        stateLabel: STATE_LABELS.settled,
        answer: LATE_SETTLEMENT.answer,
        evidence: LATE_SETTLEMENT.evidence,
        usage: LATE_SETTLEMENT.usage,
        charge: LATE_SETTLEMENT.charge,
        amountToman: LATE_SETTLEMENT.amountToman,
        amountLabel: LATE_SETTLEMENT.amountLabel,
      };
    },

    dispose() {
      disposed = true;
    },

    async getActivation() {
      require('ai.wallet.read', 'ai.settings.manage', 'ai.run.execute');
      return activation();
    },

    async setOptIn(next) {
      require('ai.activation.manage');
      optedIn = next;
      return activation();
    },

    async claimWelcomeCredit() {
      return {
        status: 'disabled' as const,
        program: 'tenant-welcome-credit',
        amountToman: null,
        amountRial: null,
        amountMicroCredits: null,
        conversionRate: null,
        conversionVersion: null,
        operationId: null,
        bucketId: null,
        grantedAt: null,
      };
    },

    async getBalance(): Promise<AiBalance> {
      require('ai.wallet.read');
      const availableMoney = moneyFrom(available, conversion);
      return {
        unit: 'toman',
        conversion,
        available: availableMoney,
        reserved: moneyFrom(openHolds, conversion),
        amountToman: availableMoney.toman,
        amountLabel: availableMoney.toman === null ? null : formatAmountToman(availableMoney.toman),
      };
    },

    async getBudget(): Promise<readonly AiBudget[]> {
      require('ai.budget.manage');
      const remaining = hard - consumed - openHolds;
      return [
        {
          scope: 'tenant',
          scopeKey: 'fixture-tenant',
          period: { start: '2026-10-01', end: '2026-11-01', zone: 'Asia/Tehran' },
          hard: moneyFrom(hard, conversion),
          soft: moneyFrom(soft, conversion),
          consumed: moneyFrom(consumed, conversion),
          openHolds: moneyFrom(openHolds, conversion),
          remaining: moneyFrom(remaining < 0n ? 0n : remaining, conversion),
          hardCredits: (hard / 1_000_000n).toString(),
          softCredits: (soft / 1_000_000n).toString(),
        },
      ];
    },

    async updateBudget(update: AiBudgetUpdate): Promise<readonly AiBudget[]> {
      require('ai.budget.manage');
      hard = BigInt(update.hardCredits) * 1_000_000n;
      soft = BigInt(update.softCredits) * 1_000_000n;
      return this.getBudget();
    },

    async getSettings(): Promise<AiSettings> {
      require('ai.settings.manage');
      return settingsView();
    },

    async updateSettings(update: AiSettingsUpdate): Promise<AiSettings> {
      require('ai.settings.manage');
      const forbidden = Object.keys(update).filter((key) => key !== 'retentionDays');
      if (forbidden.length > 0) invalid('SETTINGS_FIELD_FORBIDDEN', forbidden[0]);
      const days = update.retentionDays;
      if (!Number.isInteger(days) || days < 1 || days > MAX_RETENTION_DAYS) {
        invalid('INVALID_RETENTION', 'retentionDays');
      }
      if (days > retentionDays) invalid('RETENTION_CANNOT_INCREASE', 'retentionDays');
      retentionDays = days;
      return settingsView();
    },

    async createQuote(request: AiQuoteRequest): Promise<AiQuote> {
      requireAll('ai.run.execute', 'inventory.stock.read');
      ready();
      const extra = Object.keys(request).filter(
        (key) =>
          key !== 'question' &&
          key !== 'tool' &&
          key !== 'domain' &&
          key !== 'conversationId',
      );
      if (extra.length > 0) invalid('BROWSER_ESTIMATE_REJECTED', extra[0]);
      const question = request.question.trim();
      const sufficient = stock.length > 0;
      // The estimate is the scenario's explicit figure (see fixtures.ts), not a computed price.
      const customer = sufficient ? 16n : 0n;
      const money = moneyFrom(customer, conversion);
      const quoteId = nextId('quote');
      const expiresAtMs = now().getTime() + QUOTE_TTL_MS;
      const quote: AiQuote = {
        quoteId,
        expiresAt: new Date(expiresAtMs).toISOString(),
        featureCode: AI_FEATURE_ERP_ANALYSIS,
        providerCode: 'fixture',
        modelCode: 'fixture-small',
        modelVersion: '1',
        dataSufficient: sufficient,
        amountToman: sufficient ? money.toman : '0',
        amountLabel: sufficient && money.toman !== null ? formatAmountToman(money.toman) : null,
        customerMicroCredits: customer.toString(),
        scopeFingerprint: fingerprintOf(question),
        message: sufficient ? null : AI_NO_DATA_MESSAGE,
        providerCalled: false,
        confirmationRequired: true,
      };
      quotes.set(quoteId, { ...quote, actor, expiresAtMs, question });
      return quote;
    },

    async createRun(request: AiRunRequest): Promise<AiTurn> {
      requireAll('ai.run.execute', 'inventory.stock.read');
      ready();
      // The same key returns the stored turn - even after the quote expired -
      // and never calls the provider again. A key used for another quote conflicts.
      const stored = runsByKey.get(request.idempotencyKey);
      if (stored) {
        if (stored.quoteId !== request.quoteId) conflict('IDEMPOTENCY_CONFLICT');
        return { ...stored.turn, replayed: true };
      }

      const quote = quotes.get(request.quoteId);
      if (!quote) reject(404, 'NOT_FOUND', 'QUOTE_NOT_FOUND', 'quoteId');
      if (quote.actor !== actor) reject(403, 'FORBIDDEN', 'QUOTE_ACTOR_MISMATCH', 'quoteId');
      // A NEW key on an expired quote is rejected; expiry only blocks new executions.
      if (now().getTime() > quote.expiresAtMs) conflict('QUOTE_EXPIRED');
      // The stock set may have changed since the quote → a new quote is required.
      if (fingerprintOf(quote.question) !== quote.scopeFingerprint) conflict('QUOTE_SCOPE_CHANGED');

      const estimate = BigInt(quote.customerMicroCredits);
      const outcome: FixtureScenario = quote.dataSufficient ? scenario : 'no-data';
      if (outcome !== 'no-data') {
        // Reserve: balance and budget are checked together, atomically.
        if (available - openHolds < estimate) conflict('INSUFFICIENT_AI_CREDIT');
        if (hard - consumed - openHolds < estimate) conflict('BUDGET_HARD_STOP');
      }
      const turn = turnFromScenario(outcome, {
        turnId: nextId('turn'),
        runId: nextId('run'),
        quoteId: quote.quoteId,
        correlationId: nextId('corr'),
      });
      const spec = FIXTURE_SCENARIOS[outcome];
      if (outcome === 'pending') {
        openHolds += BigInt(spec.settlement.held.microCredits); // the hold stays until late settlement
      } else if (outcome === 'settled' && turn.charge) {
        const charge = BigInt(turn.charge.microCredits);
        available -= charge;
        consumed += charge;
      }
      const record: RunRecord = {
        idempotencyKey: request.idempotencyKey,
        quoteId: quote.quoteId,
        createdAt: now().toISOString(),
        turn,
        execution: spec.execution,
        settlement: spec.settlement,
      };
      runsByKey.set(request.idempotencyKey, record);
      turnsById.set(turn.turnId, record);
      if (turn.runId) runsById.set(turn.runId, record);
      ordered.unshift(record);
      if (loseNext) {
        loseNext = false;
        throw new AiNetworkError(true); // recorded on the "server", response lost
      }
      return turn;
    },

    async getRun(runId: string): Promise<AiRunStatus> {
      requireAll('ai.usage.read', 'inventory.stock.read');
      const record = runsById.get(runId);
      if (!record) reject(404, 'NOT_FOUND', 'RUN_NOT_FOUND', 'runId');
      return statusOf(record);
    },

    async getRunByIdempotencyKey(key: string): Promise<AiRunRecovery> {
      requireAll('ai.usage.read', 'inventory.stock.read');
      const record = runsByKey.get(key);
      if (!record) reject(404, 'NOT_FOUND', 'RUN_NOT_FOUND', 'idempotencyKey');
      const { turn } = record;
      return {
        idempotencyKey: key,
        runId: turn.runId,
        turnId: turn.turnId,
        state: turn.state,
        stateLabel: turn.stateLabel,
        execution: record.execution,
        settlement: record.settlement,
        usage: turn.usage,
      };
    },

    async getTurn(turnId: string): Promise<AiTurn> {
      requireAll('ai.usage.read', 'inventory.stock.read');
      const record = turnsById.get(turnId);
      if (!record) reject(404, 'NOT_FOUND', 'TURN_NOT_FOUND', 'turnId');
      return record.turn;
    },

    async getUsage(scope: AiUsageScope): Promise<AiUsageResponse> {
      require(scope === 'all' ? 'ai.usage.read-all' : 'ai.usage.read');
      return {
        scope,
        items: ordered.slice(0, 50).map((record) => ({
          turnId: record.turn.turnId,
          runId: record.turn.runId,
          quoteId: record.quoteId,
          correlationId: record.turn.correlationId,
          createdAt: record.createdAt,
          execution: record.execution,
          settlement: record.settlement,
          state: record.turn.state,
          stateLabel: record.turn.stateLabel,
          usage: record.turn.usage,
          charge: record.turn.charge,
          amountToman: record.turn.amountToman,
          amountLabel: record.turn.amountLabel,
        })),
      };
    },

    async getWalletPackages(): Promise<AiWalletPackagesResponse> {
      require('ai.wallet.read');
      return {
        pricingMode: 'raw_tokens',
        packages: [
          {
            id: 'base',
            title: 'بسته پایه',
            amountToman: '1000000',
            amountRial: '10000000',
            amountMicroCredits: '1000000000',
            tokenCount: '5000000',
            description: 'برای گفت‌وگو و تحلیل‌های روزمره',
            badge: null,
          },
          {
            id: 'pro',
            title: 'بسته حرفه‌ای',
            amountToman: '3000000',
            amountRial: '30000000',
            amountMicroCredits: '3000000000',
            tokenCount: '15000000',
            description: 'برای استفاده مستمر تیم‌ها',
            badge: 'پیشنهادی',
          },
          {
            id: 'enterprise',
            title: 'بسته سازمانی',
            amountToman: '5000000',
            amountRial: '50000000',
            amountMicroCredits: '5000000000',
            tokenCount: '25000000',
            description: 'برای استفاده گسترده سازمانی',
            badge: null,
          },
        ],
        bounds: {
          minToman: '1000000',
          maxToman: '100000000',
          conversionRate: '1000',
        },
      };
    },

    async createTopupCheckout(request: AiTopupCheckoutRequest): Promise<AiTopupCheckoutResponse> {
      require('ai.wallet.read');
      const authority = 'A00000000000000000000000000000000000';
      return {
        orderId: 'order-fixture-1',
        authority,
        paymentUrl: `https://payment.zarinpal.com/pg/StartPay/${authority}`,
        amountToman: request.amountToman,
        amountRial: String(BigInt(request.amountToman) * 10n),
        amountMicroCredits: String(BigInt(request.amountToman) * 100n),
        conversionRate: '100',
      };
    },

    async verifyTopup(_authority: string, status?: string): Promise<AiTopupVerifyResponse> {
      require('ai.wallet.read');
      if (status && status.toUpperCase() === 'NOK') {
        return {
          orderId: 'order-fixture-1',
          status: 'cancelled',
          message: 'پرداخت لغو شد.',
          refId: null,
          amountToman: null,
          amountMicroCredits: null,
          alreadyVerified: false,
        };
      }
      return {
        orderId: 'order-fixture-1',
        status: 'paid',
        message: 'پرداخت با موفقیت انجام شد.',
        refId: '12345678',
        amountToman: '100000',
        amountMicroCredits: '10000000',
        alreadyVerified: false,
      };
    },

    async reconcileTopup(orderId: string): Promise<AiTopupVerifyResponse> {
      require('ai.wallet.read');
      return {
        orderId,
        status: 'pending',
        message: 'این مسیر نمایشی است و پرداخت واقعی را بررسی نمی‌کند.',
        refId: null,
        amountToman: null,
        amountMicroCredits: null,
        alreadyVerified: false,
      };
    },

    async getTopupOrders(): Promise<readonly AiTopupOrder[]> {
      require('ai.wallet.read');
      return [];
    },
  };
}
